"use client";
import { usePathname } from "next/navigation";
import React, { createContext, useContext } from "react";
import type { Translation } from "@/hooks/localization/getTranslation";

type TLang = "ar" | "en";
type TProps = {
  children: React.ReactNode;
  lang: TLang;
  /** Store default locale (basic_language), server-provided so SSR/client agree. */
  defaultLocale?: TLang;
  /** Raw NEXT_LOCALE cookie value ("" when unset) — see {@link useLangCookie}. */
  langCookie?: string;
  /**
   * The ACTIVE locale's dictionary only, resolved on the server.
   *
   * PERF: this is why client components no longer import the dictionaries. Both
   * used to ship in an every-page chunk because the lookup was by runtime key;
   * the server knows the locale per request, so it sends one. See the note in
   * `hooks/localization/getTranslation.ts`.
   */
  messages: Translation;
};
type TLangContextValue = TLang;

const LangContext = createContext<null | TLangContextValue>(null);
const DefaultLocaleContext = createContext<TLang | null>(null);
const LangCookieContext = createContext<string>("");
const TranslationsContext = createContext<Translation | null>(null);

/**
 * Current locale from the URL's first segment; falls back to the store default
 * for the unprefixed (default-locale) paths. This is invariant to the proxy's
 * rewrite (which only ever ADDS the default-locale prefix), so SSR and client
 * agree.
 */
const localeFromPathname = (pathname: string | null, dflt: TLang): TLang => {
  const seg = pathname?.split("/")[1];
  return seg === "ar" || seg === "en" ? seg : dflt;
};

const LangProvider: React.FC<TProps> = ({
  children,
  lang: initLang,
  defaultLocale,
  langCookie = "",
  messages,
}) => {
  // Derive the current locale from the URL (reactive on client navigation) rather
  // than trusting the server-provided `lang`. The `[lang]` layout that mounts this
  // provider is shared and does NOT re-render on soft navigations, so a static
  // `lang` prop would freeze at the first-load value and every <Link> would build
  // hrefs for the wrong locale — the language would "never stick". `usePathname`
  // updates on every navigation, keeping useLang()/<Link> aligned with the address bar.
  const dflt = defaultLocale ?? initLang;
  const pathname = usePathname();
  const lang = localeFromPathname(pathname, dflt);

  return (
    <LangContext.Provider value={lang}>
      <DefaultLocaleContext.Provider value={dflt}>
        <LangCookieContext.Provider value={langCookie}>
          <TranslationsContext.Provider value={messages}>
            {children}
          </TranslationsContext.Provider>
        </LangCookieContext.Provider>
      </DefaultLocaleContext.Provider>
    </LangContext.Provider>
  );
};

export default LangProvider;

export const useLang = () => {
  const LangContextValue = useContext(LangContext);
  if (!LangContextValue) {
    throw Error("useLang should be call inside LangProvider");
  } else {
    return LangContextValue;
  }
};

/**
 * The store's default locale (basic_language). Server-provided so SSR and the first client
 * render compute the same value — used by <Link> to decide whether to prefix the current
 * lang. Falls back to the current lang (→ no prefix) when not provided.
 */
export const useDefaultLocale = (): TLang => {
  const lang = useLang();
  return useContext(DefaultLocaleContext) ?? lang;
};

export const useLangCookie = (): string => useContext(LangCookieContext);

/**
 * The active locale's dictionary. Backs {@link useTranslation} — call that
 * instead; this exists so the context stays private to the provider.
 *
 * Throws outside `LangProvider`, matching {@link useLang}. Nothing can call this
 * from outside today: `useTranslation` has always gone through `useLang`, which
 * already threw there (the error pages under `app/` are the only components
 * mounted outside this provider, and none of them translate).
 */
export const useMessages = (): Translation => {
  const messages = useContext(TranslationsContext);
  if (!messages) {
    throw Error("useTranslation should be called inside LangProvider");
  }
  return messages;
};
